import { FormEvent, useEffect, useState } from "react";
import { api } from "@/api";
import type { CoachMemory as Memory } from "@/lib/types";
import { useUI } from "@/lib/theme";

const KINDS = ["injury", "preference", "mark", "constraint", "equipment", "note"] as const;
type Kind = (typeof KINDS)[number];

function kindLabel(t: (k: string) => string, kind: string): string {
  return t(`coach_mem_kind_${KINDS.includes(kind as Kind) ? kind : "note"}`);
}

/**
 * Memoria durable del atleta para el Coach IA: hechos que el atleta gestiona a mano (lesiones,
 * preferencias, marcas…) y que el Coach inyecta en su contexto entre conversaciones. Sección
 * colapsable para no recargar la tab del Coach. Los candidatos extraídos por IA (pendientes de
 * revisión) llegan en un paso posterior y se mostrarán aquí mismo.
 */
export default function CoachMemory() {
  const { t } = useUI();
  const [open, setOpen] = useState(false);
  const [memories, setMemories] = useState<Memory[]>([]);
  const [pending, setPending] = useState<Memory[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [kind, setKind] = useState<Kind>("note");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");

  async function reload() {
    const [active, pend] = await Promise.all([api.coachMemory("active"), api.coachMemory("pending")]);
    setMemories(active.memories);
    setPending(pend.memories);
    setLoaded(true);
  }

  async function approve(id: string) {
    if (busy) return;
    setBusy(true);
    try {
      await api.updateCoachMemory(id, { status: "active" });
      await reload();
    } finally {
      setBusy(false);
    }
  }

  async function discard(id: string) {
    if (busy) return;
    setBusy(true);
    try {
      await api.deleteCoachMemory(id);
      await reload();
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void reload().catch(() => setLoaded(true));
  }, []);

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!content.trim() || busy) return;
    setBusy(true);
    try {
      await api.createCoachMemory({ kind, content });
      setContent("");
      setKind("note");
      await reload();
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit(id: string) {
    if (!editContent.trim() || busy) return;
    setBusy(true);
    try {
      await api.updateCoachMemory(id, { content: editContent });
      setEditingId(null);
      await reload();
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!window.confirm(t("coach_memory_delete_confirm")) || busy) return;
    setBusy(true);
    try {
      await api.deleteCoachMemory(id);
      await reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 rounded-xl border border-line bg-panel2/30 p-3">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted">
          {t("coach_memory_title")} {loaded && memories.length > 0 ? `· ${memories.length}` : ""}
          {pending.length > 0 && (
            <span className="rounded-md bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-semibold normal-case text-violet-500">
              {t("coach_memory_review_badge", { n: pending.length })}
            </span>
          )}
        </span>
        <span className="text-muted">{open ? "▾" : "▸"}</span>
      </button>

      {open && (
        <div className="mt-3">
          <p className="text-xs text-muted">{t("coach_memory_desc")}</p>

          {pending.length > 0 && (
            <div className="mt-3 rounded-lg border border-violet-500/40 bg-violet-500/5 p-2.5">
              <div className="text-[11px] font-bold uppercase tracking-wider text-violet-500">
                {t("coach_memory_pending_title")} · {pending.length}
              </div>
              <p className="mt-0.5 text-xs text-muted">{t("coach_memory_pending_desc")}</p>
              <ul className="mt-2 space-y-1.5">
                {pending.map((m) => (
                  <li
                    key={m.id}
                    className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-panel px-2.5 py-1.5"
                  >
                    <span className="rounded-md bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-violet-500">
                      {kindLabel(t, m.kind)}
                    </span>
                    <span className="min-w-0 flex-1 break-words text-sm text-fg">{m.content}</span>
                    <button
                      onClick={() => approve(m.id)}
                      disabled={busy}
                      className="text-xs font-semibold text-accent hover:underline disabled:opacity-40"
                    >
                      {t("coach_memory_approve")}
                    </button>
                    <button
                      onClick={() => discard(m.id)}
                      disabled={busy}
                      className="text-xs font-semibold text-muted hover:text-fg disabled:opacity-40"
                    >
                      {t("coach_memory_discard")}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {loaded && memories.length === 0 && (
            <p className="mt-2 text-sm text-muted">{t("coach_memory_empty")}</p>
          )}

          <ul className="mt-2 space-y-1.5">
            {memories.map((m) => (
              <li
                key={m.id}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-panel px-2.5 py-1.5"
              >
                <span className="rounded-md bg-accent/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-accent">
                  {kindLabel(t, m.kind)}
                </span>
                {editingId === m.id ? (
                  <>
                    <input
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="min-w-0 flex-1 rounded-md border border-line bg-panel px-2 py-1 text-sm outline-none focus:border-accent"
                    />
                    <button
                      onClick={() => saveEdit(m.id)}
                      disabled={busy || !editContent.trim()}
                      className="text-xs font-semibold text-accent disabled:opacity-40"
                    >
                      {t("coach_memory_save")}
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="text-xs font-semibold text-muted hover:text-fg"
                    >
                      {t("coach_cancel_goal")}
                    </button>
                  </>
                ) : (
                  <>
                    <span className="min-w-0 flex-1 break-words text-sm text-fg">{m.content}</span>
                    <button
                      onClick={() => {
                        setEditingId(m.id);
                        setEditContent(m.content);
                      }}
                      className="text-xs font-semibold text-muted hover:text-fg"
                    >
                      {t("coach_edit_goal")}
                    </button>
                    <button
                      onClick={() => remove(m.id)}
                      className="text-xs font-semibold text-red-500 hover:underline"
                    >
                      {t("coach_delete_goal")}
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>

          <form onSubmit={add} className="mt-3 flex flex-wrap gap-2">
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as Kind)}
              className="rounded-lg border border-line bg-panel px-2 py-2 text-sm outline-none focus:border-accent"
            >
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {kindLabel(t, k)}
                </option>
              ))}
            </select>
            <input
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t("coach_memory_placeholder")}
              className="min-w-0 flex-1 rounded-lg border border-line bg-panel px-3 py-2 text-sm outline-none focus:border-accent"
            />
            <button
              disabled={busy || !content.trim()}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
            >
              {t("coach_memory_add")}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
